/*
  Central API client for browser-side calls.
  - jsonFetch: unified fetch with JSON parsing and normalized errors
  - withTimeout: request timeout wrapper
  - request events: subscribe to before/after events for debug panel
  - token helpers: admin/owner/metrics tokens
  - tiny in-memory LRU cache (for metadata endpoints)
  - metrics key whitelist
*/

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface JsonFetchOptions extends RequestInit {
  method?: HttpMethod;
  timeoutMs?: number;
  // When true, don't throw on non-2xx; return { ok:false, status, data }
  soft?: boolean;
  // Attach tokens automatically
  useAdminToken?: boolean;
  useOwnerToken?: boolean;
  bearerToken?: string | null | undefined;
}

export interface JsonFetchResponse<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  url: string;
}

export interface NormalizedError extends Error {
  status?: number;
  url?: string;
  data?: any;
  context?: any;
}

// Simple request event emitter
export type RequestEvent = {
  id: string;
  url: string;
  method: HttpMethod;
  startedAt: number;
  finishedAt?: number;
  ok?: boolean;
  status?: number;
};

type Listener = (e: RequestEvent) => void;
const listeners = new Set<Listener>();
export function addRequestListener(fn: Listener) { listeners.add(fn); }
export function removeRequestListener(fn: Listener) { listeners.delete(fn); }

function emitStart(e: RequestEvent) { listeners.forEach(l => { try { l(e); } catch {} }); }
function emitEnd(e: RequestEvent) { listeners.forEach(l => { try { l(e); } catch {} }); }

// Token helpers
export function getAdminToken(): string | null {
  try { return localStorage.getItem("dev_admin_token"); } catch { return null; }
}
export function getOwnerToken(): string | null {
  try { return localStorage.getItem("owner_token"); } catch { return null; }
}
export function getMetricsToken(): string | null {
  try {
    return (
      localStorage.getItem("METRICS_BEARER") ||
      localStorage.getItem("metrics_bearer") ||
      localStorage.getItem("dev_metrics_bearer") ||
      (window as any)?.__METRICS_BEARER__ ||
      null
    );
  } catch {
    return null;
  }
}

// LRU cache (tiny)
class LRU<V> {
  private map = new Map<string, V>();
  constructor(private capacity = 50) {}
  get(key: string): V | undefined {
    if (!this.map.has(key)) return undefined;
    const v = this.map.get(key)!;
    this.map.delete(key);
    this.map.set(key, v);
    return v;
  }
  set(key: string, value: V) {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size > this.capacity) {
      const first = this.map.keys().next().value as string | undefined;
      if (typeof first === 'string') this.map.delete(first);
    }
  }
}
export const apiCache = new LRU<any>(100);

export const METRIC_KEYS = new Set([
  "views","shares","contactClicks","messageClicks","socialClicks","saves","qrScans",
  "emailClicks","callClicks","websiteClicks","shareClicks","uniqueVisitors","conversions","conversionRate"
]);

function withTimeout<T>(p: Promise<T>, timeoutMs = 15000, controller?: AbortController): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      try { controller?.abort(); } catch {}
      const err: NormalizedError = new Error("Request timeout");
      err.status = 0; reject(err);
    }, timeoutMs);
    p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
  });
}

function normalizeHeaders(init?: HeadersInit): Record<string, string> {
  const out: Record<string, string> = {};
  if (init) {
    const h = new Headers(init as any);
    h.forEach((v, k) => { out[k] = v; });
  }
  return out;
}

export async function jsonFetch<T = any>(url: string, opts: JsonFetchOptions = {}): Promise<JsonFetchResponse<T>> {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const method: HttpMethod = (opts.method || "GET").toUpperCase() as HttpMethod;
  const controller = new AbortController();
  const { soft, timeoutMs = 20000, useAdminToken, useOwnerToken, bearerToken, ...rest } = opts;

  const headers = normalizeHeaders(rest.headers);
  if (!headers["Accept"]) headers["Accept"] = "application/json";
  if (!headers["Content-Type"] && rest.body && typeof rest.body === "string") headers["Content-Type"] = "application/json";

  let token: string | null | undefined = bearerToken;
  if (token == null && useAdminToken) token = getAdminToken();
  if (token == null && useOwnerToken) token = getOwnerToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const startedAt = Date.now();
  emitStart({ id, url, method, startedAt });

  try {
    const resp = await withTimeout(fetch(url, { ...rest, method, headers, signal: controller.signal }), timeoutMs, controller);
    const status = resp.status;
    let data: any = null;
    const ct = resp.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) data = await resp.json(); else data = await resp.text();
    } catch { data = null; }

    const ok = resp.ok;
    const result: JsonFetchResponse<T> = { ok, status, data, url };

    emitEnd({ id, url, method, startedAt, finishedAt: Date.now(), ok, status });

    if (!ok && !soft) {
      const err: NormalizedError = new Error((data && (data.error || data.message)) || `HTTP ${status}`);
      err.status = status; err.url = url; err.data = data; throw err;
    }
    return result;
  } catch (e: any) {
    emitEnd({ id, url, method, startedAt, finishedAt: Date.now(), ok: false, status: e?.status ?? 0 });
    if (soft) return { ok: false, status: e?.status ?? 0, data: e?.data ?? null, url } as JsonFetchResponse<T>;
    const err: NormalizedError = e instanceof Error ? e : new Error("Request failed");
    if (e && typeof e === "object") {
      if (e.status != null) (err as any).status = e.status;
      if (e.url != null) (err as any).url = e.url;
      if (e.data != null) (err as any).data = e.data;
    }
    throw err;
  }
}

// Convenience helpers
export function get<T = any>(url: string, opts: JsonFetchOptions = {}) { return jsonFetch<T>(url, { ...opts, method: "GET" }); }
export function post<T = any>(url: string, body?: any, opts: JsonFetchOptions = {}) { return jsonFetch<T>(url, { ...opts, method: "POST", body: body ?? undefined }); }
export function put<T = any>(url: string, body?: any, opts: JsonFetchOptions = {}) { return jsonFetch<T>(url, { ...opts, method: "PUT", body: body ?? undefined }); }
export function patch<T = any>(url: string, body?: any, opts: JsonFetchOptions = {}) { return jsonFetch<T>(url, { ...opts, method: "PATCH", body: body ?? undefined }); }
export function del<T = any>(url: string, opts: JsonFetchOptions = {}) { return jsonFetch<T>(url, { ...opts, method: "DELETE" }); }

// JSON body helper
export function json(body: any) { return JSON.stringify(body ?? {}); }

// Poll helper with exponential backoff
export async function poll<T>(fn: () => Promise<T>, opts: { timeoutMs?: number; initialDelayMs?: number; factor?: number } = {}) {
  const timeoutMs = opts.timeoutMs ?? 30000;
  const initialDelayMs = opts.initialDelayMs ?? 2500;
  const factor = opts.factor ?? 1.8;
  let elapsed = 0; let n = 0;
  let last: any;
  while (elapsed < timeoutMs) {
    last = await fn().catch(() => undefined);
    if (last !== undefined) return last;
    const delay = Math.min(Math.floor(initialDelayMs * Math.pow(factor, n++)), timeoutMs - elapsed);
    await new Promise(r => setTimeout(r, delay));
    elapsed += delay;
  }
  return last;
}
